import {
    closestCenter,
    DndContext,
    type DragEndEvent,
    type DragStartEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { refetchTasklistsMessageSchema, socketEvent } from "@orsane/schema/realtime";
import { useParams } from "@tanstack/react-router";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu } from "@/components/ui/sidebar";
import { useSocketMessage } from "@/lib/socket/SocketContext";
import { updateTaskListOrderMutation } from "@/mutations/task-list-mutations";
import { useSuspenseTaskLists } from "@/queries/list-queries";
import { SortableSidebarMenuItem } from "./SortableSidebarMenuItem";

function EmptyTextBubble() {
    return (
        <div className="select-none absolute bottom-16 left-3 right-1 bg-white rounded-lg p-3 shadow-xs border text-gray-700 after:content-[''] after:absolute after:top-full after:left-3 after:border-l-[8px] after:border-r-[8px] after:border-t-[8px] after:border-l-transparent after:border-r-transparent after:border-t-white after:drop-shadow-xs text-center z-50">
            You have no tasklists yet. <br />
            Create one to get started.
        </div>
    );
}

export const TaskListsSection = memo(() => {
    const { data, refetch } = useSuspenseTaskLists();
    const taskListId = useParams({ strict: false, select: (p: any) => Number(p.taskListId) || undefined });
    const { mutate: updateTaskListOrder } = updateTaskListOrderMutation();
    const [lists, setLists] = useState(data);

    useSocketMessage({
        event: socketEvent.refetch_tasklists,
        schema: refetchTasklistsMessageSchema,
        callback: () => {
            refetch();
        },
    });

    // For some reasong an update without the effect fucks up the drag drop sorting. TODO: figure out why.
    useEffect(() => {
        if (data !== lists) {
            setLists(data);
        }
    }, [data]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5, // Small distance to prevent accidental drags but allow easy drag start
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragStart = useCallback((_event: DragStartEvent) => {
        // Disable pointer events on all links during drag
        document.body.style.setProperty("pointer-events", "none");
    }, []);

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            // Re-enable pointer events
            document.body.style.setProperty("pointer-events", "");

            if (!event.active || !event.over) return;
            if (event.active.id === event.over.id) return;

            const oldIndex = lists.findIndex((item) => item.id.toString() === event.active.id.toString());
            const newIndex = lists.findIndex((item) => item.id.toString() === event.over!.id.toString());

            if (oldIndex !== -1 && newIndex !== -1) {
                // Create the new order array
                const reorderedData = arrayMove(lists, oldIndex, newIndex);
                const newOrderIds = reorderedData.map((item) => item.id);

                // Update the server with the new order
                setLists(reorderedData); // local state update to persist the order. don't do that via react query or we get weird in-between renders
                updateTaskListOrder({ orderedIds: newOrderIds });
            }
        },
        [lists, updateTaskListOrder]
    );

    const sortableItems = useMemo(() => lists.map((list) => list.id.toString()), [lists]);

    if (!lists.length) {
        return <EmptyTextBubble />;
    }

    return (
        <SidebarGroup className="pr-0">
            <SidebarGroupLabel>Lists</SidebarGroupLabel>
            <SidebarGroupContent>
                <SidebarMenu className="gap-0.5">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                        onDragStart={handleDragStart}
                        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                    >
                        <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
                            {lists.map((list) => (
                                <SortableSidebarMenuItem
                                    key={list.id}
                                    list={list}
                                    currentTaskListId={taskListId}
                                    id={list.id.toString()}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                </SidebarMenu>
            </SidebarGroupContent>
        </SidebarGroup>
    );
});
TaskListsSection.displayName = "TaskListsSection";
